import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [isStarted, setIsStarted] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (sessionId && isOpen) {
      const interval = setInterval(() => {
        fetchMessages();
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [sessionId, isOpen]);

  const startChat = async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/chat/start-session`);
      setSessionId(response.data.session_id);
      setIsStarted(true);
    } catch (error) {
      console.error('Error starting chat:', error);
    }
  };

  const fetchMessages = async () => {
    if (!sessionId) return;
    try {
      const response = await axios.get(`${BACKEND_URL}/api/chat/messages/${sessionId}`);
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !sessionId) return;

    try {
      await axios.post(`${BACKEND_URL}/api/chat/send-message`, {
        session_id: sessionId,
        message: inputMessage,
        user_name: userName || 'Guest',
        user_email: userEmail
      });
      setInputMessage('');
      fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-purple-600 hover:bg-purple-700 text-white rounded-full p-4 shadow-lg transition-all hover:scale-110"
        >
          <MessageCircle size={24} />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <Card className="fixed bottom-6 right-6 z-50 w-96 h-[600px] flex flex-col bg-gray-900 border-white/20 shadow-2xl">
          {/* Header */}
          <div className="bg-purple-600 p-4 flex items-center justify-between rounded-t-lg">
            <div className="flex items-center">
              <MessageCircle size={20} className="mr-2" />
              <span className="font-bold text-white">Live Chat</span>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-white hover:bg-purple-700 rounded p-1">
              <X size={20} />
            </button>
          </div>

          {/* Chat Content */}
          {!isStarted ? (
            <div className="flex-1 p-6 flex flex-col justify-center">
              <h3 className="text-white font-bold text-lg mb-4">Start a conversation</h3>
              <Input
                placeholder="Your name (optional)"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                className="mb-3 bg-black/50 border-white/10 text-white"
              />
              <Input
                placeholder="Your email (optional)"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                className="mb-4 bg-black/50 border-white/10 text-white"
              />
              <Button onClick={startChat} className="bg-purple-600 hover:bg-purple-700 text-white">
                Start Chat
              </Button>
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.length === 0 && (
                  <div className="text-center text-gray-400 mt-8">
                    <p>Send a message to start chatting!</p>
                  </div>
                )}
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg p-3 ${
                        msg.sender === 'user'
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-800 text-white'
                      }`}
                    >
                      {msg.sender === 'admin' && (
                        <p className="text-xs text-purple-400 mb-1">Support Team</p>
                      )}
                      <p className="text-sm break-words">{msg.message}</p>
                      <p className="text-xs opacity-70 mt-1">
                        {new Date(msg.timestamp).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-4 border-t border-white/10">
                <div className="flex space-x-2">
                  <Input
                    placeholder="Type your message..."
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="bg-black/50 border-white/10 text-white"
                  />
                  <Button onClick={sendMessage} className="bg-purple-600 hover:bg-purple-700">
                    <Send size={18} />
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card>
      )}
    </>
  );
};

export default ChatWidget;